import React, { useEffect, useRef, useState } from 'react'

import PropTypes from 'prop-types'

import { Box } from '@material-ui/core'
import Chip from '@material-ui/core/Chip'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import { makeCachedStyles } from './inline-styles'

const useStyles = makeCachedStyles((theme) => ({
    chip: {
        margin: theme.spacing(0.5),
        color: 'white !important',
    },
}))

function defaultGetOptionLabel(option) {
    return option
}

function defaultGetChipProps() {
    return null
}

export function ChipMultiSelectField({
    getChipProps = defaultGetChipProps,
    getOptionLabel = defaultGetOptionLabel,
    onChange,
    options = [],
    readonly = false,
    SelectProps = {},
    value = [],
    ...rest
}) {
    const { disabled, error, fullWidth, helperText, id, label, margin, required, variant, ...selectRest } = rest

    const classes = useStyles()
    const inputLabelRef = useRef(null)
    const [labelWidth, setLabelWidth] = useState(0)
    const isDisabled = readonly || disabled
    const selectId = id || `chip-multi-select-${label || 'field'}`
    const labelId = label ? `${selectId}-label` : undefined

    useEffect(() => {
        if (inputLabelRef.current) {
            setLabelWidth(inputLabelRef.current.offsetWidth)
        }
    }, [label])

    function handleDelete(target) {
        return (event) => {
            event.stopPropagation()
            onChange?.({
                target: {
                    value: value.filter((item) => item !== target),
                },
            })
        }
    }

    return (
        <FormControl
            disabled={isDisabled || SelectProps.disabled}
            error={error}
            fullWidth={fullWidth}
            margin={margin}
            required={required}
            variant={variant}
        >
            {label ? (
                <InputLabel id={labelId} ref={inputLabelRef} shrink>
                    {label}
                </InputLabel>
            ) : null}
            <Select
                {...selectRest}
                {...SelectProps}
                disabled={isDisabled || SelectProps.disabled}
                id={selectId}
                labelId={SelectProps.labelId || labelId}
                labelWidth={SelectProps.labelWidth || labelWidth}
                notched={SelectProps.notched ?? Boolean(label)}
                onChange={onChange}
                value={value}
                multiple
                renderValue={(selected) => (
                    <Box display="flex" flexWrap="wrap">
                        {[...selected]
                            .sort((left, right) => getOptionLabel(left).localeCompare(getOptionLabel(right)))
                            .map((selectedValue) => {
                                const chipProps = getChipProps(selectedValue) || {}
                                return (
                                    <Chip
                                        key={selectedValue}
                                        label={getOptionLabel(selectedValue)}
                                        className={classes.chip}
                                        onDelete={readonly ? undefined : handleDelete(selectedValue)}
                                        {...chipProps}
                                    />
                                )
                            })}
                    </Box>
                )}
            >
                {[...options]
                    .sort((left, right) => getOptionLabel(left).localeCompare(getOptionLabel(right)))
                    .map((option) => (
                        <MenuItem key={option} value={option}>
                            {getOptionLabel(option)}
                        </MenuItem>
                    ))}
            </Select>
            {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
        </FormControl>
    )
}

ChipMultiSelectField.propTypes = {
    getChipProps: PropTypes.func,
    getOptionLabel: PropTypes.func,
    onChange: PropTypes.func,
    options: PropTypes.array,
    readonly: PropTypes.bool,
    SelectProps: PropTypes.object,
    label: PropTypes.string,
    value: PropTypes.array,
}

export default ChipMultiSelectField
